import React from 'react';
import { Platform } from 'react-native';

import { datadog } from '@sg/expo-datadog-client';

import { useOrderAppFonts } from './useOrderAppFonts';

// ─────────────────────────────────────────────────────────────────────────────
// This hook provides visibility into a known issue around expo-font loading:
// https://github.com/expo/expo/issues/21885
export function useAppFonts() {
  const [fontsLoaded, fontError] = useOrderAppFonts();
  const [hasPrevError, setHasPrevError] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (fontError && !hasPrevError) {
      reportFontError(fontError);
      setHasPrevError(true);
    }

    if (hasPrevError && !fontError) {
      console.info(`Font error resolved! ${fontsLoaded && 'Fonts loaded'}`);
      setHasPrevError(false);
    }
  }, [fontsLoaded, fontError, hasPrevError, setHasPrevError]);

  // NOTE: Web doesn't wait for fonts, since browsers swap them in once they load. In WebKit,
  //       expo-font 58's web loader never settles and only fails after its 12s timeout.
  return fontsLoaded || fontError || Platform.OS === 'web';
}

function reportFontError(fontError: Error) {
  // NOTE: WebKit hits expo-font 58's web timeout in most sessions, even though the fonts still
  //       render. Reporting it as an error keeps every one of those RUM sessions, so web only
  //       logs it at debug level.
  if (Platform.OS === 'web') {
    datadog.logger.debug(fontError.message, { issue: 'https://github.com/expo/expo/issues/21885' });

    return;
  }

  void datadog.reportError(fontError, {
    fingerprint: 'expo-font:load-error',
    context: { issue: 'https://github.com/expo/expo/issues/21885' },
  });
  console.error(getErrorMessage(fontError));
}

function getErrorMessage(fontError: Error | undefined) {
  return `
Doh! An error loading the font.
${fontError}
You may want to restart the app, by hitting "r" in the terminal or devclient.
More info about this issue here: https://github.com/expo/expo/issues/21885`;
}
